// src/pages/AssignOrders.js
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Table,
  Button,
  Tag,
  Space,
  Input,
  Select,
  Modal,
  Card,
  Typography,
  Tooltip,
  Skeleton,
  message,
} from "antd";
import { 
  EyeOutlined, 
  SearchOutlined, 
  UserAddOutlined,
  MailOutlined,
  PhoneOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
  EnvironmentOutlined,
  InboxOutlined,
  CalendarOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { Avatar } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { Row, Col } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheckCircle, FaTruck, FaUserCheck, FaUserTimes } from 'react-icons/fa';
import BASEURL from "../API/Config";

const { Title, Text } = Typography;
const { Option } = Select;

const AssignOrders = () => {
  const [orders, setOrders] = useState([]);
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState("");

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedDeliveryBoy, setSelectedDeliveryBoy] = useState("");
  const [assigning, setAssigning] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);

  const ordersApi = `${BASEURL}/user/getallorders`;
  const deliveryBoyApi = `${BASEURL}/user/getdeliverboy`;
  const assignApi = `${BASEURL}/orders/orders/assign-delivery-boy`;

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          message.error("Authentication session expired. Please login again.");
          setLoading(false);
          return;
        }

        const [ordersRes, boysRes] = await Promise.all([
          axios.get(ordersApi, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(deliveryBoyApi, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        setOrders((ordersRes.data || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        setDeliveryBoys(boysRes.data || []);
      } catch (err) {
        console.error(err);
        message.error("Failed to fetch data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ordersApi, deliveryBoyApi]);

  // Filtering logic
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        order.user?.email?.toLowerCase().includes(term) ||
        order.shippingInfo?.address?.toLowerCase().includes(term) ||
        order.trackingNumber?.toLowerCase().includes(term) ||
        order.orderStatus?.toLowerCase().includes(term);

      const matchesStatus = statusFilter === "" || order.orderStatus === statusFilter;

      const assignedBoy = deliveryBoys.find((b) => b.AssignOrders?.includes(order._id));
      const matchesAssigned =
        assignedFilter === "" || (assignedBoy && assignedBoy._id === assignedFilter);

      const matchesAssignment = 
        assignmentFilter === "" || 
        (assignmentFilter === "Assigned" ? !!assignedBoy : !assignedBoy);

      return matchesSearch && matchesStatus && matchesAssigned && matchesAssignment;
    });
  }, [orders, searchTerm, statusFilter, assignedFilter, assignmentFilter, deliveryBoys]);

  const stats = useMemo(() => {
    const total = orders.length;
    let assigned = 0;
    let unassigned = 0;
    
    orders.forEach((order) => {
      const boy = deliveryBoys.find((b) => b.AssignOrders?.includes(order._id));
      if (boy) {
        assigned++;
      } else {
        unassigned++;
      }
    });

    return { total, assigned, unassigned };
  }, [orders, deliveryBoys]);

  const handleAssignOrder = async () => {
    if (!selectedDeliveryBoy) {
      message.warning("Please select a delivery boy");
      return;
    }
    try {
      setAssigning(true);
      const token = localStorage.getItem("token");
      await axios.put(
        assignApi,
        { orderId: selectedOrderId, deliveryBoyId: selectedDeliveryBoy },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success("Delivery boy assigned successfully!");
      setOrders((prev) =>
        prev.map((o) => (o._id === selectedOrderId ? { ...o, orderStatus: "Ready to Ship" } : o))
      );
      setDeliveryBoys((prev) =>
        prev.map((boy) => {
          // Add to new delivery boy
          if (boy._id === selectedDeliveryBoy) {
            return { ...boy, AssignOrders: [...(boy.AssignOrders || []), selectedOrderId] };
          }
          // Remove from previous delivery boy if any
          return {
            ...boy,
            AssignOrders: (boy.AssignOrders || []).filter(id => id !== selectedOrderId)
          };
        })
      );
      setShowAssignModal(false);
    } catch (err) {
      console.error(err);
      message.error("Failed to assign delivery boy.");
    } finally {
      setAssigning(false);
    }
  };

  const handlePrintInvoice = async (orderId) => {
    try {
      const hide = message.loading('Generating invoice...', 0);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${BASEURL}/user/order/${orderId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      hide();
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    } catch (err) {
      message.destroy();
      console.error("Error generating invoice", err);
      message.error("Failed to generate invoice. Please try again.");
    }
  };

  const getStatusTag = (status) => {
    let color = "default";
    switch (status) {
      case "Delivered": color = "success"; break;
      case "Ordered": color = "processing"; break;
      case "Ready to Ship": color = "warning"; break;
      case "Out for Delivery": color = "magenta"; break;
      case "cancelled": color = "error"; break;
      default: color = "default";
    }
    return <Tag color={color} style={{ borderRadius: '6px', fontWeight: 600 }}>{status?.toUpperCase() || "N/A"}</Tag>;
  };

  const columns = [
    {
      title: "S.No",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Customer",
      key: "customer",
      render: (record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.shippingInfo?.firstName} {record.shippingInfo?.lastName}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.user?.email}</Text>
        </Space>
      ),
    },
    {
      title: "Address",
      dataIndex: ["shippingInfo", "address"],
      key: "address",
      ellipsis: true,
      render: (text) => <Tooltip title={text}>{text || "N/A"}</Tooltip>,
    },
    {
      title: "Status",
      dataIndex: "orderStatus",
      key: "status",
      render: (status) => getStatusTag(status),
      filters: [
        { text: 'Ordered', value: 'Ordered' },
        { text: 'Ready to Ship', value: 'Ready to Ship' },
        { text: 'Out for Delivery', value: 'Out for Delivery' },
        { text: 'Delivered', value: 'Delivered' },
        { text: 'Cancelled', value: 'cancelled' },
      ],
      onFilter: (value, record) => record.orderStatus === value,
    },
    {
      title: "Tracking No",
      dataIndex: "trackingNumber",
      key: "tracking",
      render: (text) => <Text code>{text || "N/A"}</Text>,
    },
    {
      title: "Assigned To",
      key: "assigned",
      render: (record) => {
        const boy = deliveryBoys.find((b) => b.AssignOrders?.includes(record._id));
        return boy ? (
          <Tooltip title={`Phone: ${boy.phoneNumber || 'N/A'}`}>
            <Space>
              <Avatar size="small" style={{ backgroundColor: '#87d068' }} icon={<UserOutlined />} />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text strong>{boy.firstname} {boy.lastname}</Text>
                <Text type="secondary" style={{ fontSize: '11px' }}>{boy.phoneNumber}</Text>
              </div>
            </Space>
          </Tooltip>
        ) : <Tag color="default">Unassigned</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => {
        const currentBoy = deliveryBoys.find((b) => b.AssignOrders?.includes(record._id));
        return (
          <Space>
            <Tooltip title="View Details">
              <Button 
                shape="circle" 
                icon={<EyeOutlined />} 
                onClick={() => { setViewOrder(record); setShowViewModal(true); }}
              />
            </Tooltip>
            <Tooltip title="Print Invoice">
              <Button 
                shape="circle" 
                icon={<PrinterOutlined />} 
                onClick={() => handlePrintInvoice(record._id)}
              />
            </Tooltip>
            {record.orderStatus !== "Delivered" && record.orderStatus !== "cancelled" ? (
              <Button 
                type={currentBoy ? "default" : "primary"}
                size="small" 
                icon={<UserAddOutlined />}
                onClick={() => { 
                  setSelectedOrderId(record._id); 
                  setSelectedDeliveryBoy(currentBoy?._id || ""); 
                  setShowAssignModal(true); 
                }}
              >
                {currentBoy ? "Re-assign" : "Assign"}
              </Button>
            ) : (
              <Button size="small" disabled>
                {record.orderStatus === "Delivered" ? "Completed" : "Cancelled"}
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="assign-orders-container"
    >
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Title level={2} style={{ margin: 0 }}>Assign Orders</Title>
          <Text type="secondary">Manage and assign orders to delivery personnel</Text>
        </div>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={8}>
          <Card size="small" style={{ borderRadius: 12, borderLeft: '4px solid #3b82f6', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 8px' }}>
              <div style={{ background: '#eff6ff', padding: 10, borderRadius: 8, color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaTruck style={{ fontSize: 20 }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Orders</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginTop: 2 }}>{stats.total}</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ borderRadius: 12, borderLeft: '4px solid #10b981', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 8px' }}>
              <div style={{ background: '#d1fae5', padding: 10, borderRadius: 8, color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaUserCheck style={{ fontSize: 20 }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Assigned Orders</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981', marginTop: 2 }}>{stats.assigned}</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ borderRadius: 12, borderLeft: '4px solid #ef4444', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 8px' }}>
              <div style={{ background: '#fee2e2', padding: 10, borderRadius: 8, color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaUserTimes style={{ fontSize: 20 }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Unassigned Orders</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#ef4444', marginTop: 2 }}>{stats.unassigned}</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card className="glass-card mb-4" bordered={false}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Input
              allowClear
              placeholder="Search by Email, Address, Status or Tracking Number..."
              prefix={<SearchOutlined style={{ color: 'var(--text-muted)' }} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="large"
              style={{ borderRadius: '12px' }}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="Filter by Status"
              style={{ width: '100%' }}
              size="large"
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="">All Status</Option>
              <Option value="Ordered">Ordered</Option>
              <Option value="Ready to Ship">Ready to Ship</Option>
              <Option value="Out for Delivery">Out for Delivery</Option>
              <Option value="Delivered">Delivered</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="Assignment"
              style={{ width: '100%' }}
              size="large"
              value={assignmentFilter}
              onChange={setAssignmentFilter}
            >
              <Option value="">All Orders</Option>
              <Option value="Assigned">Assigned</Option>
              <Option value="Unassigned">Unassigned</Option>
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <Select
              placeholder="Filter by Delivery Boy"
              style={{ width: '100%' }}
              size="large"
              allowClear
              showSearch
              filterOption={(input, option) =>
                String(option?.searchValue ?? '').toLowerCase().includes(input.toLowerCase())
              }
              value={assignedFilter}
              onChange={setAssignedFilter}
              popupClassName="premium-select-dropdown"
              optionLabelProp="displayName"
              options={[
                { value: "", label: "All Delivery Boys", displayName: "All Delivery Boys", searchValue: "all" },
                ...deliveryBoys.map((boy) => ({
                  value: boy._id,
                  searchValue: `${boy.firstname} ${boy.lastname} ${boy.phoneNumber} ${boy.email}`,
                  displayName: `${boy.firstname} ${boy.lastname}`,
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                      <Avatar 
                        size="small" 
                        style={{ backgroundColor: '#f1f5f9', color: '#64748b', marginRight: '10px' }}
                        icon={<UserOutlined />}
                      />
                      <div style={{ flex: 1 }}>
                        <Text strong style={{ fontSize: '13px', display: 'block' }}>{boy.firstname} {boy.lastname}</Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          <PhoneOutlined style={{ fontSize: '10px', marginRight: '4px' }} />{boy.phoneNumber}
                        </Text>
                      </div>
                    </div>
                  ),
                }))
              ]}
            />
          </Col>
        </Row>
      </Card>

      <Card className="glass-card" bordered={false} bodyStyle={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: '24px' }}>
            <Skeleton active />
            <Skeleton active />
          </div>
        ) : (
          <Table 
            columns={columns} 
            dataSource={filteredOrders} 
            rowKey="_id"
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"],
              showTotal: (total) => `Total ${total} orders`,
              style: { padding: '16px' } }}
          />
        )}
      </Card>

      <Modal
        title={
          <Space size="small">
            <UserAddOutlined style={{ color: '#4f46e5' }} />
            <span style={{ fontWeight: 700, fontSize: '15px' }}>Assign Delivery</span>
          </Space>
        }
        open={showAssignModal}
        onCancel={() => setShowAssignModal(false)}
        onOk={handleAssignOrder}
        confirmLoading={assigning}
        centered
        width={600}
        okText="Assign"
        okButtonProps={{ 
          style: { 
            borderRadius: '8px', 
            height: '34px', 
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            border: 'none',
            fontWeight: 600,
            fontSize: '13px'
          } 
        }}
        cancelButtonProps={{ style: { borderRadius: '8px', height: '34px', fontSize: '13px' } }}
      >
        <div style={{ padding: '4px 0' }}>
          <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>ORDER: <Text strong style={{ color: '#1e293b' }}>{orders.find(o => o._id === selectedOrderId)?.trackingNumber}</Text></Text>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <Input
              allowClear
              placeholder="Search personnel..."
              size="middle"
              prefix={<SearchOutlined style={{ color: '#94a3b8', fontSize: '12px' }} />}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0' }}
            />
          </div>

          <div style={{ 
            maxHeight: '260px', 
            overflowY: 'auto', 
            paddingRight: '4px',
            margin: '0 -2px'
          }}>
            <Row gutter={[0, 6]}>
              {deliveryBoys
                .filter(boy => {
                  const term = searchTerm.toLowerCase();
                  return boy.firstname.toLowerCase().includes(term) || 
                         boy.lastname.toLowerCase().includes(term) || 
                         boy.phoneNumber.toString().includes(term) ||
                         boy.email.toLowerCase().includes(term);
                })
                .map((boy) => {
                  const isSelected = selectedDeliveryBoy === boy._id;
                  const isCurrent = boy.AssignOrders?.includes(selectedOrderId);
                  
                  return (
                    <Col span={24} key={boy._id}>
                      <div 
                        onClick={() => setSelectedDeliveryBoy(boy._id)}
                        style={{ 
                          padding: '8px 10px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: isSelected ? '1.5px solid #4f46e5' : '1px solid #f1f5f9',
                          background: isSelected ? '#f5f7ff' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          position: 'relative',
                        }}
                      >
                        <Avatar 
                          size={32} 
                          style={{ 
                            backgroundColor: isSelected ? '#4f46e5' : '#f8fafc', 
                            color: isSelected ? '#fff' : '#94a3b8',
                            marginRight: '10px',
                            fontSize: '14px'
                          }}
                          icon={<UserOutlined />}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Text strong style={{ fontSize: '13px', color: isSelected ? '#4f46e5' : '#334155' }}>
                              {boy.firstname} {boy.lastname}
                            </Text>
                            {isCurrent && <Tag color="blue" style={{ margin: 0, fontSize: '9px', borderRadius: '3px', padding: '0 4px' }}>CURR</Tag>}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', marginTop: '0px' }}>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              <PhoneOutlined style={{ fontSize: '9px', marginRight: '2px' }} />{boy.phoneNumber}
                            </Text>
                          </div>
                        </div>
                        {isSelected && (
                          <div style={{ color: '#4f46e5' }}>
                            <FaCheckCircle size={16} />
                          </div>
                        )}
                      </div>
                    </Col>
                  );
                })}
              {deliveryBoys.filter(boy => {
                const term = searchTerm.toLowerCase();
                return boy.firstname.toLowerCase().includes(term) || 
                       boy.lastname.toLowerCase().includes(term) || 
                       boy.phoneNumber.toString().includes(term) ||
                       boy.email.toLowerCase().includes(term);
              }).length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 0', width: '100%' }}>
                  <Text type="secondary">No delivery personnel found matching your search.</Text>
                </div>
              )}
            </Row>
          </div>

          <div style={{ marginTop: '12px', padding: '8px 10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              <InfoCircleOutlined style={{ marginRight: '4px' }} />
              Updating will notify personnel.
            </Text>
          </div>
        </div>
      </Modal>

      {/* View Order Modal */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined style={{ color: 'var(--primary-color)' }} />
            <span>Order Details</span>
          </Space>
        }
        open={showViewModal}
        onCancel={() => setShowViewModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowViewModal(false)} style={{ borderRadius: '8px' }}>
            Close
          </Button>
        ]}
        width={800}
        centered
      >
        <AnimatePresence>
          {viewOrder && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="order-details-sections">
                <Row gutter={[16, 16]} className="mb-3">
                  <Col span={12}>
                    <Card size="small" title="Order Info" style={{ height: '100%', background: '#f8fafc' }}>
                      <Space direction="vertical" size={4}>
                        <Space><InfoCircleOutlined /><Text strong>Status:</Text> {getStatusTag(viewOrder.orderStatus)}</Space>
                        <Space><InboxOutlined /><Text strong>Tracking:</Text> <Text code>{viewOrder.trackingNumber || "N/A"}</Text></Space>
                        <Space><CalendarOutlined /><Text strong>Date:</Text> {new Date(viewOrder.createdAt).toLocaleString()}</Space>
                      </Space>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="Assignment" style={{ height: '100%', background: '#f8fafc' }}>
                      {(() => {
                        const boy = deliveryBoys.find(b => b.AssignOrders?.includes(viewOrder._id));
                        return boy ? (
                          <Space direction="vertical" size={4}>
                            <Space><UserOutlined /><Text strong>Name:</Text> {boy.firstname} {boy.lastname}</Space>
                            <Space><PhoneOutlined /><Text strong>Phone:</Text> {boy.phoneNumber}</Space>
                            <Space><MailOutlined /><Text strong>Email:</Text> {boy.email}</Space>
                          </Space>
                        ) : <Text type="secondary">Not assigned yet</Text>;
                      })()}
                    </Card>
                  </Col>
                </Row>

                <Card size="small" className="mb-3" title="Customer & Shipping" style={{ background: '#f8fafc' }}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Space><UserOutlined type="secondary" /><Text strong>Customer:</Text> {viewOrder.shippingInfo?.firstName} {viewOrder.shippingInfo?.lastName}</Space>
                    </Col>
                    <Col span={12}>
                      <Space><MailOutlined style={{ color: '#4f46e5' }} /><Text strong>Email:</Text> {viewOrder.user?.email}</Space>
                    </Col>
                    <Col span={12}>
                      <Space><PhoneOutlined style={{ color: '#10b981' }} /><Text strong>Phone:</Text> {viewOrder.phoneNumber}</Space>
                    </Col>
                    <Col span={12}>
                      <Space><CreditCardOutlined style={{ color: '#8b5cf6' }} /><Text strong>Payment:</Text> {viewOrder.paymentInfo?.cashOndelivery ? "COD" : "Paid Online"}</Space>
                    </Col>
                    <Col span={24}>
                      <Space align="start"><EnvironmentOutlined style={{ color: '#ef4444' }} /><Text strong>Address:</Text> {viewOrder.shippingInfo?.address}, {viewOrder.shippingInfo?.city}, {viewOrder.shippingInfo?.state} - {viewOrder.shippingInfo?.pincode}</Space>
                    </Col>
                  </Row>
                </Card>

                <Card size="small" title="Order Items" className="mb-3" style={{ background: '#f8fafc' }}>
                  <Table 
                    size="small"
                    pagination={false}
                    columns={[
                      {
                        title: "Image",
                        key: "image",
                        width: 80,
                        render: (record) => (
                          <div style={{ width: 40, height: 40, borderRadius: 6, overflow: 'hidden', border: '1px solid #eee' }}>
                            {record.product?.images?.[0]?.url ? (
                              <img 
                                src={`${BASEURL}${record.product.images[0].url}`} 
                                alt="product" 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                              />
                            ) : (
                              <div style={{ width: '100%', height: '100%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <InboxOutlined style={{ color: '#ccc' }} />
                              </div>
                            )}
                          </div>
                        )
                      },
                      { title: "Product", dataIndex: ["product", "title"], key: "name" },
                      { title: "Qty", dataIndex: "quantity", key: "qty" },
                      { title: "Price", dataIndex: "price", key: "price", render: (p) => `₹${p}` },
                      { title: "Total", key: "total", render: (r) => `₹${(r.price * r.quantity).toFixed(2)}` },
                    ]} 
                    dataSource={viewOrder.orderItems}
                    rowKey={(r) => r.product?._id || Math.random()}
                  />
                  <div style={{ marginTop: 16, textAlign: 'right', padding: '0 12px' }}>
                    <Text strong style={{ fontSize: 16 }}>Total Amount: <span style={{ color: '#ff6701' }}>₹{viewOrder.totalPrice?.toFixed(2)}</span></Text>
                  </div>
                </Card>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Modal>

      <style jsx="true">{`
        .assign-orders-container {
          padding-bottom: 2rem;
          background: #f8fafc;
          min-height: 100vh;
          padding: 24px;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.5);
        }
        .ant-table-thead > tr > th {
          background: #f1f5f9 !important;
          color: #475569 !important;
          font-weight: 700 !important;
          text-transform: uppercase;
          font-size: 11px;
          letter-spacing: 0.75px;
          padding: 16px !important;
        }
        .ant-table-row {
          transition: all 0.2s;
        }
        .ant-table-row:hover {
          background: #f8fafc !important;
        }
        .order-details-sections .ant-card {
          border-radius: 12px;
          border: 1px solid #e2e8f0;
        }
        .order-details-sections .ant-card-head {
          border-bottom: 1px solid #e2e8f0;
          font-weight: 700;
          background: #f8fafc;
        }
        .premium-select-dropdown {
          border-radius: 12px;
          padding: 8px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
        }
        .ant-select-item-option-selected {
          background-color: #f0f7ff !important;
          border-radius: 8px;
        }
        .ant-select-item-option {
          border-radius: 8px;
          margin-bottom: 4px;
        }
      `}</style>
    </motion.div>
  );
};

export default AssignOrders;

